import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Safely renders HTML content by sanitizing it to prevent XSS attacks
 * @param htmlContent - The HTML content to sanitize and render
 * @returns Sanitized HTML content safe for rendering
 */
export const sanitizeHtml = (htmlContent: string): string => {
  if (!htmlContent) return '';
  
  // Configure DOMPurify to allow common HTML tags used in descriptions
  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'span', 'div'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
  };
  
  return DOMPurify.sanitize(htmlContent, config);
};

/**
 * Component props for safe HTML rendering
 */
export interface SafeHtmlProps {
  content: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * React component for safely rendering HTML content
 */
export const SafeHtml: React.FC<SafeHtmlProps> = ({ content, className, style }) => {
  const sanitizedContent = sanitizeHtml(content);
  
  return (
    <div 
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};
